/**
 * Centralized TanStack Query key factory.
 *
 * All query keys live here so cache invalidation is always correct.
 * Pattern: [entity, scope, filters?]
 */

export const queryKeys = {
  // ─── Auth ───────────────────────────────────────────────────────────────
  me: () => ['me'] as const,

  // ─── Employees ──────────────────────────────────────────────────────────
  employees: {
    all: () => ['employees'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['employees', 'list', filters] as const,
    detail: (id: string) => ['employees', 'detail', id] as const,
    history: (id: string) => ['employees', 'history', id] as const,
  },

  // ─── Shifts ─────────────────────────────────────────────────────────────
  shifts: {
    all: () => ['shifts'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['shifts', 'list', filters] as const,
    detail: (id: string) => ['shifts', 'detail', id] as const,
    /** Tax calculation preview (not cached long) */
    taxPreview: (params: Record<string, unknown>) =>
      ['shifts', 'tax-preview', params] as const,
  },

  // ─── Payslips ───────────────────────────────────────────────────────────
  payslips: {
    all: () => ['payslips'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['payslips', 'list', filters] as const,
    detail: (id: string) => ['payslips', 'detail', id] as const,
  },

  // ─── Employee portal ────────────────────────────────────────────────────
  myShifts: {
    all: () => ['my-shifts'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['my-shifts', 'list', filters] as const,
  },
  myPayslips: {
    all: () => ['my-payslips'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['my-payslips', 'list', filters] as const,
  },
  myPay: {
    all: () => ['my-pay'] as const,
    list: (year: number) => ['my-pay', 'list', year] as const,
    statement: (contactId: string, period: string) => ['my-pay', 'statement', contactId, period] as const,
  },
  declarations: {
    obligations: (year: number) => ['declarations', 'obligations', year] as const,
  },
  myEarnings: {
    summary: (filters?: Record<string, unknown>) =>
      ['my-earnings', 'summary', filters] as const,
    thresholds: (filters?: Record<string, unknown>) =>
      ['my-earnings', 'thresholds', filters] as const,
    obligations: (filters: { year: string }) =>
      ['my-earnings', 'obligations', filters] as const,
  },
  employerContacts: {
    all: () => ['employer-contacts'] as const,
    list: () => ['employer-contacts', 'list'] as const,
    detail: (id: string) => ['employer-contacts', 'detail', id] as const,
  },

  // ─── Contracts (both modes; `role` is part of the key because the two
  //     routers can answer differently for a user who holds both roles) ────
  contracts: {
    all: () => ['contracts'] as const,
    list: (role: string, filters?: Record<string, unknown>) =>
      ['contracts', 'list', role, filters] as const,
    detail: (role: string, id: string) => ['contracts', 'detail', role, id] as const,
    prefill: (employeeId: string) => ['contracts', 'prefill', employeeId] as const,
  },

  // ─── Employers ──────────────────────────────────────────────────────────
  employer: {
    profile: () => ['employer', 'profile'] as const,
    taxSettings: () => ['employer', 'tax-settings'] as const,
    stats: (filters?: Record<string, unknown>) =>
      ['employer', 'stats', filters] as const,
    history: (id: string) => ['employer', 'history', id] as const,
    vavThresholds: () => ['employer', 'vav-thresholds'] as const,
  },

  // ─── Billing ────────────────────────────────────────────────────────────
  billing: {
    current: () => ['billing', 'current'] as const,
  },

  // ─── Tax ────────────────────────────────────────────────────────────────
  tax: {
    cantons: () => ['tax', 'cantons'] as const,
    rates: (canton: string, code: string) =>
      ['tax', 'rates', canton, code] as const,
    brackets: (canton: string, year: number) =>
      ['tax', 'brackets', canton, year] as const,
    health: () => ['tax', 'health'] as const,
    configuration: (year?: number) => ['tax', 'configuration', year ?? 'current'] as const,
    eligibility: (
      residencyStatus: string,
      cantonCode: string,
      annualIncome: number,
      spouseSwissOrSettled = false,
      residenceCountry: string | null = null
    ) => ['tax', 'eligibility', residencyStatus, cantonCode, annualIncome, spouseSwissOrSettled, residenceCountry] as const,
  },

  // ─── Saved reports ──────────────────────────────────────────────────────
  savedReports: {
    all:    ()           => ['saved-reports']         as const,
    list:   ()           => ['saved-reports', 'list'] as const,
    detail: (id: string) => ['saved-reports', id]     as const,
  },

  // ─── Reports ────────────────────────────────────────────────────────────
  reports: {
    all: () => ['reports'] as const,
    monthly: (filters?: Record<string, unknown>) =>
      ['reports', 'monthly', filters] as const,
    annual: (year: number, employeeId?: string) => ['reports', 'annual', year, employeeId] as const,
  },

  // ─── Users ──────────────────────────────────────────────────────────────
  user: {
    profile: () => ['user', 'profile'] as const,
    preferences: () => ['user', 'preferences'] as const,
  },

  // ─── Service types ──────────────────────────────────────────────────────
  serviceTypes: {
    all: () => ['service-types'] as const,
    list: () => ['service-types', 'list'] as const,
    detail: (id: string) => ['service-types', 'detail', id] as const,
  },

  // ─── Location ───────────────────────────────────────────────────────────
  location: {
    postalCode: (code: string) => ['location', 'postal-code', code] as const,
  },

  // ─── Admin ──────────────────────────────────────────────────────────────
  admin: {
    taxHealth: () => ['admin', 'tax-health'] as const,
    businessKpis: () => ['admin', 'business-kpis'] as const,
    summary: () => ['admin', 'analytics-summary'] as const,
    activity: () => ['admin', 'analytics-activity'] as const,
    features: () => ['admin', 'analytics-features'] as const,
    cantonDistribution: () => ['admin', 'canton-distribution'] as const,
    recentSignups: () => ['admin', 'recent-signups'] as const,
    signupTrend: () => ['admin', 'signup-trend'] as const,
    activationFunnel: () => ['admin', 'activation-funnel'] as const,
    retention: () => ['admin', 'retention'] as const,
    billingSummary: () => ['admin', 'billing-summary'] as const,
    trialsExpiring: () => ['admin', 'trials-expiring'] as const,
    // Tax operations
    taxYears: () => ['admin', 'tax', 'years'] as const,
    taxReference: (year: number) => ['admin', 'tax', 'reference', year] as const,
    taxVerification: (year: number, scope: string) => ['admin', 'tax', 'verification', year, scope] as const,
    taxDiff: (yearOld: number, yearNew: number) => ['admin', 'tax', 'diff', yearOld, yearNew] as const,
    taxEnvironment: () => ['admin', 'tax', 'environment'] as const,
    auditLogs: (filters: Record<string, unknown>) => ['admin', 'audit-logs', filters] as const,
    taxAttestations: (year: number) => ['admin', 'tax', 'attestations', year] as const,
    taxYearChange: (year: number) => ['admin', 'tax', 'year-change', year] as const,
    taxReadiness: (year: number) => ['admin', 'tax', 'readiness', year] as const,
    taxEstvWatch: (year: number) => ['admin', 'tax', 'estv-watch', year] as const,
  },

  // ─── Privacy / legal ────────────────────────────────────────────────────
  privacy: {
    notice: (lang: string) => ['privacy', 'notice', lang] as const,
  },

  // ─── Onboarding / Setup Guide ─────────────────────────────────────────────
  onboarding: {
    guide: (role?: string) => ['onboarding', 'guide', role] as const,
  },

  // ─── Support threads (two-way messaging) ──────────────────────────────────
  support: {
    myThreads: (page?: number) => ['support', 'my-threads', page] as const,
    myThread: (id: string) => ['support', 'my-thread', id] as const,
    // Admin thread detail shares the ['admin','feedback'] prefix so a reply
    // invalidates the admin inbox list too.
    adminThread: (id: string) => ['admin', 'feedback', 'thread', id] as const,
  },

  // ─── Announcements ─────────────────────────────────────────────────────────
  announcements: {
    active: () => ['announcements', 'active'] as const,
    adminList: (page?: number) => ['admin', 'announcements', page] as const,
    adminDetail: (id: string) => ['admin', 'announcements', 'detail', id] as const,
  },
} as const;
